import React from 'react';

import InputLabel from '@components/InputLabel/index';
import { HorizontalFlex } from '@components/RecordCard/RecordCard.style';

import useUniqueId from '@hooks/useUniqueId';

import { formatNumber } from '@utils/format';

import {
  ErrorMessage,
  SuccessMessage,
  TextInputStyle,
  WarningMessage,
  CharCount,
} from './Textarea.style';

export default function TextArea({
  id = '',
  label = '',
  required = false,
  onChange,
  message = '',
  value = '',
  variant = 'error',
  width = '',
  height = 'auto',
  charLimit = 1000,
  expandable = true,
  ...props
}: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value'> & {
  id?: string;
  label?: string;
  message?: string;
  variant?: string;
  textarea?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  width?: string;
  value?: string | null;
  height?: number | string;
  required?: boolean;
  charLimit?: number;
  expandable?: boolean;
}) {
  const [charCount, setCharCount] = React.useState(0);
  const uniqueId = useUniqueId('input');

  React.useEffect(() => {
    setCharCount(value?.length ?? 0);
  }, [value]);

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const { value } = event.target;
    const isValid = (charLimit && value.length <= charLimit) || !charLimit;
    if (onChange && isValid) {
      onChange(event);
    }
  }

  return (
    <TextInputStyle expandable={expandable} width={width}>
      <InputLabel
        inputId={id || uniqueId}
        label={label}
        required={required}
        style={{ color: 'primary.main' }}
      />

      <textarea
        className={message ? variant : undefined}
        id={id || uniqueId}
        onChange={handleChange}
        rows={Math.floor(charCount / 50)}
        style={{ height: height || 'auto' }}
        value={value ? value : ''}
        {...props}
      />
      <HorizontalFlex
        marginBottom={0}
        marginLeft={0}
        marginRight={0}
        marginTop={0}
      >
        <div>
          {Boolean(message) && variant === 'error' ? (
            <ErrorMessage>{message}</ErrorMessage>
          ) : null}
          {Boolean(message) && variant === 'warning' ? (
            <WarningMessage>{message}</WarningMessage>
          ) : null}
          {Boolean(message) && variant === 'success' ? (
            <SuccessMessage>{message}</SuccessMessage>
          ) : null}
        </div>
        {charLimit ? (
          <CharCount>{`${formatNumber(charCount)}/${formatNumber(
            charLimit
          )}`}</CharCount>
        ) : null}
      </HorizontalFlex>
    </TextInputStyle>
  );
}
